import React, { useState, useEffect } from 'react';
import { Grid3X3, Plus, Upload, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { gridAPI } from '../../services/api';
import AssetSelect from '../../components/AssetSelect';
import { useAuth } from '../../context/AuthContext';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [{ id:'logs',label:'Production Logs' },{ id:'curtailment',label:'Curtailment' },{ id:'tariffs',label:'Feed-in Tariffs' },{ id:'offtake',label:'Offtake' }];
const REASON_COLOR = { grid_constraint:'bg-red-100 text-red-700', market:'bg-amber-100 text-amber-700', regulatory:'bg-blue-100 text-blue-700', technical:'bg-purple-100 text-purple-700', other:'bg-gray-100 text-gray-600' };

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;
const S = ({ children, ...p }) => <select {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">{children}</select>;

function Modal({ title, onClose, children, onSubmit, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">{children}</div>
        {onSubmit && <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={onSubmit} disabled={loading} className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
            {loading&&<Loader2 size={14} className="animate-spin"/>} Save
          </button>
        </div>}
      </div>
    </div>
  );
}

export default function GridPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('super_admin','operations_mgr');

  const [tab, setTab]           = useState('logs');
  const [logs, setLogs]         = useState([]);
  const [curtailment, setCurtailment] = useState([]);
  const [tariffs, setTariffs]   = useState([]);
  const [offtake, setOfftake]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [logModal, setLogModal] = useState(false);
  const [curtModal, setCurtModal] = useState(false);
  const [csvFile, setCsvFile]   = useState(null);
  const [importing, setImporting] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary]   = useState('');
  const [saving, setSaving]     = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const [lForm, setLForm] = useState({ asset_id:'', log_date: today, production_mwh:'', consumption_mwh:'', availability_pct:'' });
  const [cForm, setCForm] = useState({ asset_id:'', event_date: today, curtailed_mwh:'', reason:'grid_constraint', financial_impact:'', notes:'' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [lg, cu, ta, of] = await Promise.all([gridAPI.logs({ limit:50 }), gridAPI.curtailment(), gridAPI.feedInTariffs(), gridAPI.offtake()]);
      setLogs(lg.data.data.items || lg.data.data);
      setCurtailment(cu.data.data);
      setTariffs(ta.data.data);
      setOfftake(of.data.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const saveLog = async () => {
    setSaving(true);
    try { await gridAPI.addLog(lForm); toast.success('Production log added'); setLogModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const saveCurtailment = async () => {
    setSaving(true);
    try { await gridAPI.addCurtailment(cForm); toast.success('Curtailment event logged'); setCurtModal(false); loadAll(); }
    catch {} finally { setSaving(false); }
  };

  const handleCsvImport = async () => {
    if (!csvFile) { toast.error('Select a CSV file first'); return; }
    const fd = new FormData();
    fd.append('file', csvFile);
    setImporting(true);
    try {
      const { data } = await gridAPI.importCSV(fd);
      toast.success(`${data.data.imported} rows imported`);
      setCsvFile(null);
      loadAll();
    } catch {} finally { setImporting(false); }
  };

  const generateSummary = async () => {
    setSummaryLoading(true);
    const now = new Date();
    try {
      const { data } = await gridAPI.generateSummary({ year: now.getFullYear(), month: now.getMonth()+1 });
      setSummary(data.data.summary);
      toast.success('Monthly summary generated');
    } catch {} finally { setSummaryLoading(false); }
  };

  // Chart data — last 14 days of logs
  const chartData = logs.slice(0,14).reverse().map(l=>({ date: l.log_date?.split('T')[0]?.slice(5), production: parseFloat(l.production_mwh||0) }));

  // Totals
  const totalProduction = logs.reduce((a,l)=>a+parseFloat(l.production_mwh||0),0);
  const totalCurtailed  = curtailment.reduce((a,c)=>a+parseFloat(c.curtailed_mwh||0),0);
  const totalImpact     = curtailment.reduce((a,c)=>a+parseFloat(c.financial_impact||0),0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Grid Operations</h1>
          <p className="text-sm text-gray-500">Production logs · Curtailment · Offtake agreements</p>
        </div>
        <div className="flex gap-2">
          {canWrite && <>
            <button onClick={generateSummary} disabled={summaryLoading} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50">
              {summaryLoading?<Loader2 size={14} className="animate-spin"/>:<Sparkles size={14}/>} Monthly Summary
            </button>
            <button onClick={()=>setLogModal(true)} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={14}/>Log Production</button>
          </>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[['Total Production (Recent)',`${totalProduction.toLocaleString(undefined,{maximumFractionDigits:0})} MWh`,'bg-emerald-50'],
          ['Total Curtailed',`${totalCurtailed.toLocaleString(undefined,{maximumFractionDigits:0})} MWh`,'bg-amber-50'],
          ['Curtailment Impact',`€${totalImpact.toLocaleString(undefined,{maximumFractionDigits:0})}`,'bg-red-50']
        ].map(([l,v,bg])=>(
          <div key={l} className={clsx('rounded-xl border border-gray-200 p-5',bg)}>
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{l}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{v}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      {chartData.length>0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Recent Production (MWh)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="date" fontSize={11}/>
              <YAxis fontSize={11}/>
              <Tooltip/>
              <Area type="monotone" dataKey="production" stroke="#1D6B4E" fill="#1D6B4E" fillOpacity={0.15} name="MWh"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* AI Summary */}
      {summary && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-primary-500"/><h3 className="text-sm font-semibold text-gray-700">Monthly Operations Summary</h3></div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={clsx('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab===t.id?'border-primary-500 text-primary-600':'border-transparent text-gray-500 hover:text-gray-700')}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Production Logs */}
      {tab==='logs' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Production Logs</h3>
            {canWrite && <div className="flex items-center gap-2">
              <label className={clsx('flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer',importing&&'opacity-60')}>
                {importing?<Loader2 size={12} className="animate-spin"/>:<Upload size={12}/>} {csvFile?csvFile.name:'Import CSV'}
                <input type="file" accept=".csv" className="hidden" onChange={e=>setCsvFile(e.target.files[0])}/>
              </label>
              {csvFile && <button onClick={handleCsvImport} disabled={importing} className="px-3 py-1.5 text-xs bg-primary-500 text-white rounded-lg hover:bg-primary-600">Import</button>}
            </div>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Date','Asset','Production MWh','Consumption MWh','Export MWh','Availability %','Source'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading?<tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 mx-auto"/></td></tr>
                :logs.length===0?<tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No production logs yet.</td></tr>
                :logs.slice(0,50).map((l,i)=>(
                  <tr key={l.id} className={i%2===0?'bg-white':'bg-surface'}>
                    <td className="px-4 py-3 text-gray-600">{l.log_date?.split('T')[0]}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{l.asset_name}</td>
                    <td className="px-4 py-3 font-medium text-primary-700">{Number(l.production_mwh).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3 text-gray-600">{l.consumption_mwh||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.export_mwh||'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{l.availability_pct?`${l.availability_pct}%`:'—'}</td>
                    <td className="px-4 py-3 text-gray-400 capitalize">{l.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Curtailment */}
      {tab==='curtailment' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Curtailment Events</h3>
            {canWrite && <button onClick={()=>setCurtModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"><Plus size={12}/>Log Event</button>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>{['Date','Asset','Curtailed MWh','Reason','Financial Impact','Duration h'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {curtailment.length===0?<tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No curtailment events logged.</td></tr>
                :curtailment.map((c,i)=>(
                  <tr key={c.id} className={i%2===0?'bg-white':'bg-surface'}>
                    <td className="px-4 py-3 text-gray-600">{new Date(c.event_date).toLocaleDateString('en-GB')}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{c.asset_name}</td>
                    <td className="px-4 py-3 font-medium text-amber-700">{Number(c.curtailed_mwh).toLocaleString(undefined,{maximumFractionDigits:2})}</td>
                    <td className="px-4 py-3"><span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',REASON_COLOR[c.reason])}>{c.reason?.replace('_',' ')}</span></td>
                    <td className="px-4 py-3 text-danger font-medium">{c.financial_impact?`€${Number(c.financial_impact).toLocaleString()}`:'—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.duration_hours||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Feed-in Tariffs */}
      {tab==='tariffs' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">Feed-in Tariff Rates</h3></div>
          {tariffs.length===0?<div className="text-center py-12 text-gray-400 text-sm">No tariff rates configured.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>{['Name','Territory','Asset Type','Rate €/MWh','Valid From','Valid To'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{tariffs.map((t,i)=>(
              <tr key={t.id} className={i%2===0?'bg-white':'bg-surface'}>
                <td className="px-4 py-3 font-medium text-gray-900">{t.name}</td>
                <td className="px-4 py-3 text-gray-600">{t.territory}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{t.asset_type}</td>
                <td className="px-4 py-3 font-medium text-primary-700">€{t.rate_eur_mwh}</td>
                <td className="px-4 py-3 text-gray-600">{new Date(t.valid_from).toLocaleDateString('en-GB')}</td>
                <td className="px-4 py-3 text-gray-600">{t.valid_to?new Date(t.valid_to).toLocaleDateString('en-GB'):'Ongoing'}</td>
              </tr>
            ))}</tbody>
          </table>}
        </div>
      )}

      {/* Offtake */}
      {tab==='offtake' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">Offtake Agreements</h3></div>
          {offtake.length===0?<div className="text-center py-12 text-gray-400 text-sm">No offtake agreements.</div>
          :<table className="w-full text-sm"><thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>{['Asset','Counterparty','Volume MWh','Price €/MWh','Start','End','Status'].map(h=><th key={h} className="px-4 py-3 text-left font-medium">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{offtake.map((o,i)=>(
              <tr key={o.id} className={i%2===0?'bg-white':'bg-surface'}>
                <td className="px-4 py-3 font-medium text-gray-900">{o.asset_name}</td>
                <td className="px-4 py-3 text-gray-600">{o.counterparty_name}</td>
                <td className="px-4 py-3 text-gray-600">{o.volume_mwh?Number(o.volume_mwh).toLocaleString():'—'}</td>
                <td className="px-4 py-3 text-gray-600">{o.price_eur_mwh?`€${o.price_eur_mwh}`:'—'}</td>
                <td className="px-4 py-3 text-gray-600">{new Date(o.start_date).toLocaleDateString('en-GB')}</td>
                <td className="px-4 py-3 text-gray-600">{new Date(o.end_date).toLocaleDateString('en-GB')}</td>
                <td className="px-4 py-3 capitalize text-gray-600">{o.status}</td>
              </tr>
            ))}</tbody>
          </table>}
        </div>
      )}

      {/* Log Production Modal */}
      {logModal && <Modal title="Log Production" onClose={()=>setLogModal(false)} onSubmit={saveLog} loading={saving}>
        <F label="Asset"><AssetSelect value={lForm.asset_id} onChange={e=>setLForm(f=>({...f,asset_id:e.target.value}))}/></F>
        <F label="Date"><I type="date" value={lForm.log_date} onChange={e=>setLForm(f=>({...f,log_date:e.target.value}))}/></F>
        <F label="Production (MWh)"><I type="number" step="0.0001" value={lForm.production_mwh} onChange={e=>setLForm(f=>({...f,production_mwh:e.target.value}))}/></F>
        <F label="Consumption (MWh)"><I type="number" step="0.0001" value={lForm.consumption_mwh} onChange={e=>setLForm(f=>({...f,consumption_mwh:e.target.value}))}/></F>
        <F label="Availability %"><I type="number" step="0.01" value={lForm.availability_pct} onChange={e=>setLForm(f=>({...f,availability_pct:e.target.value}))}/></F>
      </Modal>}

      {/* Curtailment Modal */}
      {curtModal && <Modal title="Log Curtailment Event" onClose={()=>setCurtModal(false)} onSubmit={saveCurtailment} loading={saving}>
        <F label="Asset"><AssetSelect value={cForm.asset_id} onChange={e=>setCForm(f=>({...f,asset_id:e.target.value}))}/></F>
        <F label="Date"><I type="date" value={cForm.event_date} onChange={e=>setCForm(f=>({...f,event_date:e.target.value}))}/></F>
        <F label="Curtailed (MWh)"><I type="number" step="0.0001" value={cForm.curtailed_mwh} onChange={e=>setCForm(f=>({...f,curtailed_mwh:e.target.value}))}/></F>
        <F label="Reason"><S value={cForm.reason} onChange={e=>setCForm(f=>({...f,reason:e.target.value}))}>
          <option value="grid_constraint">Grid Constraint</option>
          <option value="market">Market</option>
          <option value="regulatory">Regulatory</option>
          <option value="technical">Technical</option>
          <option value="other">Other</option>
        </S></F>
        <F label="Financial Impact (€)"><I type="number" step="0.01" value={cForm.financial_impact} onChange={e=>setCForm(f=>({...f,financial_impact:e.target.value}))}/></F>
        <F label="Notes"><textarea value={cForm.notes} onChange={e=>setCForm(f=>({...f,notes:e.target.value}))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/30"/></F>
      </Modal>}
    </div>
  );
}
